// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * User menu with profile indicator, settings, and admin controls.
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { InGameIndicator } from './InGameIndicator';
import { useActiveSession, triggerActiveSessionRefresh } from '../hooks/useActiveSession';
import { api } from '../lib/api';
import { logger } from '../lib/logger';
import { useAuthStore } from '../store/authStore';
import { useGameStore } from '../store/gameStore';
import { useThemeStore } from '../store/themeStore';

interface UserMenuProps {
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
}

export function UserMenu({ isAdmin = false, isSuperAdmin = false }: UserMenuProps) {
  const navigate = useNavigate();
  const { displayName, clearSession, gameState: _gameState } = useGameStore();
  const { resetToDefault } = useThemeStore();
  const { user, clearAuth, isAuthenticated, setShowSignedOutModal } = useAuthStore();
  const { activeSession } = useActiveSession();
  const [isOpen, setIsOpen] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Check if we're in a WARP game
  const iswarp = activeSession?.game_id === 'warp';

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut: "^" (Shift+6) toggles advanced mode
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Check for "^" key (Shift+6 or caret character)
      if (e.key === '^' || (e.shiftKey && e.key === '6')) {
        e.preventDefault();
        setAdvancedMode(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Get indicator color based on user's theme or role
  const getIndicatorColor = () => {
    // Use user's theme color if available (authenticated users)
    if (user?.theme_primary_color) {
      return null; // Will use inline style instead
    }
    // Fall back to role-based colors
    if (isSuperAdmin) return 'bg-red-500';
    if (isAdmin) return 'bg-amber-500';
    if (isAuthenticated()) return 'bg-purple-500';
    return 'bg-gray-200';
  };

  // Get inline style for custom theme color
  const getIndicatorStyle = () => {
    if (user?.theme_primary_color) {
      return { backgroundColor: user.theme_primary_color };
    }
    return {};
  };

  // Get initials from display name or username
  const getInitials = () => {
    const name = user?.username || displayName;
    if (!name) return null;
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Get display content (emoji or initials)
  const getDisplayContent = () => {
    // If user is authenticated and has emoji, show it
    if (user?.profile_emoji) {
      return user.profile_emoji;
    }
    // Default emoji for authenticated users without a custom emoji
    if (user) {
      return '👤';
    }
    // For guest users, show initials if available, else default person
    return getInitials() || '👤';
  };

  // Handle logout
  const handleLogout = async () => {
    // Leave any active session first (notifies other players)
    try {
      await api.leaveMySession();
    } catch {
      // Ignore - might not have an active session
    }

    try {
      await api.logout();
    } catch {
      // Ignore errors, clear auth anyway
    }
    clearAuth();
    clearSession(); // Also clear game session to prevent auto-joining old game
    triggerActiveSessionRefresh(); // Clear the in-game indicator
    setIsOpen(false);
    setShowSignedOutModal(true);
    navigate('/'); // Go back to home page
  };

  const handleLeaveSession = async () => {
    if (
      confirm(
        'Are you sure you want to leave your current game? This will remove you from the session and allow you to join or create a new one.'
      )
    ) {
      try {
        await api.leaveMySession();
      } catch (error) {
        logger.error('failed_to_leave_session', { error });
      } finally {
        // Always trigger a refresh and clear local state
        triggerActiveSessionRefresh();
        clearSession();
        setIsOpen(false);
        navigate('/'); // Go back to home page
      }
    }
  };

  const handleResetTheme = () => {
    if (confirm('Are you sure you want to reset the theme to default?')) {
      resetToDefault();
      setIsOpen(false);
    }
  };

  const handleClearStorage = () => {
    if (
      confirm(
        'Are you sure you want to clear all local storage? This will log you out and reset all preferences.'
      )
    ) {
      localStorage.clear();
      clearAuth();
      clearSession();
      setIsOpen(false);
      navigate('/', { replace: true });
    }
  };

  return (
    <div ref={menuRef} className="relative flex items-center gap-2">
      {/* In-Game Indicator (hidden for WARP) */}
      {activeSession && !iswarp && (
        <InGameIndicator
          session={activeSession}
          onClick={async () => {
            // Restore session context before navigating
            try {
              // Get session with token for WebSocket auth
              const response = await api.getMySession(true);
              if (response.active_session?.player_token) {
                const session = response.active_session;
                // Restore gameStore context
                useGameStore
                  .getState()
                  .setSession(
                    session.session_id,
                    session.room_code,
                    session.player_id,
                    session.player_token ?? '',
                    user?.username || 'Player'
                  );
                logger.info('session_restored', {
                  sessionId: session.session_id,
                  playerId: session.player_id,
                });
              }
            } catch (err) {
              logger.error('session_restore_failed', { error: err });
              // Still navigate - the WebSocket may fail but at least try
            }
            // Navigate to lobby or game based on phase
            const route = activeSession.phase === 'lobby' ? 'lobby' : 'game';
            navigate(`/${route}/${activeSession.session_id}`);
          }}
        />
      )}

      {/* User Indicator Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-11 h-11 rounded-lg flex items-center justify-center font-bold transition-all hover:scale-105 ${
          iswarp
            ? 'text-xl'
            : `text-white ${user?.profile_emoji || user ? 'text-2xl' : 'text-sm'} ${getIndicatorColor() || ''}`
        }`}
        style={
          iswarp
            ? {
                backgroundColor: 'var(--color-surface)',
                color: 'var(--color-text-primary)',
                border: '2px solid var(--color-glass-border)',
                boxShadow: '2px 2px 0px var(--neo-shadow-color)',
              }
            : {
                ...getIndicatorStyle(),
                boxShadow: '2px 2px 0px var(--neo-shadow-color)',
                border: '2px solid var(--neo-border-color)',
              }
        }
        title={displayName || 'User Settings'}
        data-testid="user-menu-button"
      >
        {iswarp ? '☰' : getDisplayContent()}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute right-0 top-14 w-64 rounded-lg z-[100]"
          style={{
            backgroundColor: 'var(--color-surface)',
            border: '2px solid var(--neo-border-color)',
            boxShadow: '3px 3px 0px var(--neo-shadow-color)',
          }}
        >
          {/* User Info */}
          <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--color-glass-border)' }}>
            <p
              className="font-semibold"
              style={{ color: 'var(--color-text-primary)' }}
              data-testid="user-menu-username"
            >
              {user?.username || displayName || 'Guest'}
            </p>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {isSuperAdmin
                ? 'Super Admin'
                : isAdmin
                  ? 'Admin'
                  : isAuthenticated()
                    ? 'Authenticated'
                    : 'Guest'}
            </p>
            {/* Room Code for WARP */}
            {iswarp && activeSession?.room_code && (
              <p
                className="text-xs mt-2 font-mono"
                style={{ color: 'var(--color-text-secondary)' }}
                title="Room Code"
              >
                {activeSession.room_code}
              </p>
            )}
            {/* Namespace indicator for test isolation */}
            {user?.namespace && (
              <p
                className="text-xs mt-1 px-2 py-0.5 rounded inline-block"
                style={{
                  backgroundColor: 'var(--color-warning)',
                  color: 'var(--color-text-on-primary)',
                }}
                title="This session is using a test namespace for isolation"
              >
                NS: {user.namespace}
              </p>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {/* Login/Register (only for guests) */}
            {!isAuthenticated() && (
              <>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/login');
                  }}
                  className="block w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/10"
                  style={{ color: 'var(--color-primary)' }}
                >
                  Sign In
                </button>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/register');
                  }}
                  className="block w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/10"
                  style={{ color: 'var(--color-success)' }}
                >
                  Create Account
                </button>
                <div
                  className="my-2 border-t"
                  style={{ borderColor: 'var(--color-glass-border)' }}
                />
              </>
            )}

            {/* User Settings */}
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/settings');
              }}
              className="block w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/10"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Settings
            </button>

            {/* Admin Panel (only for admins) */}
            {(isAdmin || isSuperAdmin || user?.permissions?.includes('admin')) && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/admin');
                }}
                className="block w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/10"
                style={{ color: 'var(--color-warning)' }}
              >
                Admin Panel
              </button>
            )}

            {/* Divider */}
            <div className="my-2 border-t" style={{ borderColor: 'var(--color-glass-border)' }} />

            {/* Leave Session (if authenticated) */}
            {isAuthenticated() && (
              <button
                onClick={handleLeaveSession}
                className="block w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/10"
                style={{ color: 'var(--color-warning)' }}
                title="If you are stuck in a game, this will force-leave your session."
              >
                Leave Current Game
              </button>
            )}

            {/* Logout (only for authenticated users) */}
            {isAuthenticated() && (
              <button
                onClick={handleLogout}
                className="block w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/10"
                style={{ color: 'var(--color-error)' }}
                data-testid="user-menu-sign-out"
              >
                Sign Out
              </button>
            )}

            {/* Advanced Settings - Only shown when "^" is pressed */}
            {advancedMode && (
              <>
                <div
                  className="my-2 border-t"
                  style={{ borderColor: 'var(--color-glass-border)' }}
                />
                <div
                  className="px-4 py-1 text-xs font-semibold"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Advanced Settings
                </div>

                {/* Reset Theme */}
                <button
                  onClick={handleResetTheme}
                  className="block w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/10"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  Reset Theme to Default
                </button>

                {/* Clear Storage */}
                <button
                  onClick={handleClearStorage}
                  className="block w-full text-left px-4 py-2 text-sm transition-colors hover:bg-white/10"
                  style={{ color: 'var(--color-error)' }}
                >
                  Clear Local Storage
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
